import type React from "react"
import { useState, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Paperclip, Send, File, MapPin, Mic, Play, Pause, X } from "lucide-react"
import api from "../utils/api"
import initializeEcho from "../utils/echo"
import ChatConfirmationModal from "./ChatConfirmationModal"
import LocationPicker from "./LocationMarker"
import AudioRecordingModal from "./AudioRecordingModal"

interface Message {
  id: number
  service_order_id: number
  sender_id: number
  sender_type: string
  message_type: string
  content: string
  file_path: string | null
  latitude?: number
  longitude?: number
  created_at: string
  agent: {
    first_name: string
    last_name: string
  }
  need_confirmation?: number
  confirmed?: number
  confirmed_name?: string
  confirmed_at?: string
}

interface Order {
  id: number
  client_id: number
  vehicle_id: number
  service_id: number
  technicien_id: number | null
  status: string
  description: string
  created_at: string
  updated_at: string
  client: {
    first_name: string
    last_name: string
    email: string
    phone: string
    avatar: string
  }
  company: {
    raison_social: string
    email: string
    phone: string
    avatar: string
    latitude: number | null
    longitude: number | null
  }
  vehicle: {
    brand: {
      name: string
      icon: string
    }
    model: string
    year: number
    plate_number: string
  }
  service: {
    name: string
    base_price: number
  }
  technician: {
    first_name: string
    last_name: string
    avatar: string
  } | null
  final_price: number
  invoice: {
    id: number
    file_path: string
  }
}

interface ChatProps {
  order: Order
}

const Chat: React.FC<ChatProps> = ({ order }) => {
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState("")
  const [attachment, setAttachment] = useState<File | null>(null)
  const { t } = useTranslation()
  const [echo, setEcho] = useState<any>(null)
  const [previewFile, setPreviewFile] = useState<{ uri: string; type: string } | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [tempAttachment, setTempAttachment] = useState<File | null>(null)
  const [needsConfirmation, setNeedsConfirmation] = useState(false)
  const [filePreview, setFilePreview] = useState<string | null>(null)
  const [fileType, setFileType] = useState<string>("")
  const [showLocationPicker, setShowLocationPicker] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [audioURL, setAudioURL] = useState<string | null>(null)
  const [isListening, setIsListening] = useState(false)
  const [audioRecorder, setAudioRecorder] = useState<MediaRecorder | null>(null)
  const [audioChunks, setAudioChunks] = useState<Blob[]>([])
  const [showAudioModal, setShowAudioModal] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const chatMessagesRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetchMessages()
    const setupEcho = async () => {
      const echoInstance = await initializeEcho()
      setEcho(echoInstance)
    }
    setupEcho()
    return () => {
      if (echo) {
        echo.disconnect()
      }
    }
  }, [order.id])

  useEffect(() => {
    if (echo) {
      const channelName = `chat.${order.id}`
      const sub = echo.private(channelName).listen("NewMessageSent", async () => {
        await fetchMessages()
      })
      return () => {
        sub.stopListening("NewMessageSent")
      }
    }
  }, [echo, order.id])

  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight
    }
  }, [messages])

  const fetchMessages = async () => {
    try {
      const response = await api.get(`/service-orders/${order.id}/chat`)
      setMessages(response.data)
    } catch (error) {
      console.error("Error fetching messages:", error)
      showToastMessage("Failed to fetch messages")
    }
  }

  const handleSendMessage = async () => {
    if (newMessage.trim() || attachment) {
      setIsSending(true)
      try {
        if (attachment && ["image", "video", "application"].includes(attachment.type.split("/")[0])) {
          setTempAttachment(attachment)
          setShowConfirmModal(true)
        } else {
          await sendMessage(needsConfirmation)
        }
      } catch (error) {
        console.error("Error sending message:", error)
        showToastMessage("Failed to send message")
      } finally {
        setIsSending(false)
      }
    }
  }

  const sendMessage = async (needsConfirmation: boolean, attachment?: File | null, audioBlob?: Blob | null) => {
    setIsSending(true)
    try {
      const formData = new FormData()
      formData.append("content", newMessage)
      formData.append("need_confirmation", needsConfirmation ? "1" : "0")

      if (attachment) {
        const fileType = attachment.type.split("/")[0]
        formData.append("message_type", fileType === "image" ? "image" : fileType === "video" ? "video" : "file")
        formData.append("file", attachment)
      } else if (audioBlob) {
        formData.append("message_type", "audio")
        formData.append("file", audioBlob, "audio_message.webm")
      } else {
        formData.append("message_type", "text")
      }

      await api.post(`/service-orders/${order.id}/chat`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      setNewMessage("")
      setAttachment(null)
      setTempAttachment(null)
      setNeedsConfirmation(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
      await fetchMessages()
    } catch (error) {
      console.error("Error sending message:", error)
      showToastMessage("Failed to send message")
    } finally {
      setIsSending(false)
    }
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0]
      if (["image", "video", "application"].includes(file.type.split("/")[0])) {
        setTempAttachment(file)
        setFileType(file.type)
        if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
          const reader = new FileReader()
          reader.onload = (e) => {
            setFilePreview(e.target?.result as string)
          }
          reader.readAsDataURL(file)
        } else {
          setFilePreview(null)
        }
        setShowConfirmModal(true)
      } else {
        setAttachment(file)
      }
    }
  }

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream)
      setAudioRecorder(mediaRecorder)

      mediaRecorder.addEventListener("dataavailable", (event) => {
        setAudioChunks((prevChunks) => [...prevChunks, event.data])
      })

      mediaRecorder.addEventListener("stop", () => {
        const audioBlob = new Blob(audioChunks, { type: "audio/webm" })
        setAudioBlob(audioBlob)
        setAudioChunks([])
      })

      mediaRecorder.start()
      setIsRecording(true)

      // Automatically stop recording after 60 seconds
      setTimeout(() => {
        if (mediaRecorder.state === "recording") {
          mediaRecorder.stop()
          setIsRecording(false)
        }
      }, 60000)
    } catch (error) {
      console.error("Error starting recording:", error)
      showToastMessage("Failed to start recording")
    }
  }

  const stopRecording = () => {
    if (audioRecorder && audioRecorder.state === "recording") {
      audioRecorder.stop()
      setIsRecording(false)
    }
  }

  const cancelRecording = () => {
    if (audioRecorder && audioRecorder.state === "recording") {
      audioRecorder.stop()
    }
    setAudioBlob(null)
    setAudioChunks([])
    setIsRecording(false)
  }

  const sendAudioMessage = async () => {
    if (audioBlob) {
      try {
        const formData = new FormData()
        formData.append("message_type", "audio")
        formData.append("file", audioBlob, "audio_message.webm")
        formData.append("need_confirmation", needsConfirmation ? "1" : "0")

        await api.post(`/service-orders/${order.id}/chat`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        setAudioBlob(null)
        setIsListening(false)
        await fetchMessages()
      } catch (error) {
        console.error("Error sending audio message:", error)
        showToastMessage("Failed to send audio message")
      }
    }
  }

  const playAudio = (url: string) => {
    if (currentAudio) {
      currentAudio.pause()
    }
    const audio = new Audio(url)
    audio.play()
    setCurrentAudio(audio)
    setIsPlaying(true)
    audio.onended = () => {
      setIsPlaying(false)
      setCurrentAudio(null)
    }
  }

  const pauseAudio = () => {
    if (currentAudio) {
      currentAudio.pause()
      setIsPlaying(false)
    }
  }

  const playRecordedAudio = () => {
    if (audioBlob) {
      const audioUrl = URL.createObjectURL(audioBlob)
      const audio = new Audio(audioUrl)
      audio.play()
      setIsListening(true)
      audio.onended = () => {
        setIsListening(false)
        URL.revokeObjectURL(audioUrl)
      }
    }
  }

  const cancelRecordedAudio = () => {
    setAudioBlob(null)
    setIsListening(false)
  }

  const showToastMessage = (message: string) => {
    setToastMessage(message)
    setShowToast(true)
    setTimeout(() => setShowToast(false), 3000)
  }

  const renderMessageContent = (message: Message) => {
    switch (message.message_type) {
      case "text":
        return <p>{message.content}</p>
      case "image":
        return (
          <div className="flex justify-end">
            <img
              src={`${process.env.REACT_APP_PIC_STORAGE}/${message.file_path}`}
              alt="Attached image"
              className="max-w-[200px] w-auto h-auto cursor-pointer object-contain"
              onClick={() =>
                setPreviewFile({ uri: `${process.env.REACT_APP_PIC_STORAGE}/${message.file_path}`, type: "image" })
              }
            />
          </div>
        )
      case "video":
        return (
          <video className="max-w-[25%] h-auto" controls>
            <source src={`${process.env.REACT_APP_PIC_STORAGE}/${message.file_path}`} />
            Your browser does not support the video tag.
          </video>
        )
      case "file":
        return (
          <a
            href={`${process.env.REACT_APP_PIC_STORAGE}/${message.file_path}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-blue-500 hover:underline"
          >
            <File size={16} className="mr-2" />
            {message.content}
          </a>
        )
      case "audio":
        return (
          <div className="flex items-center">
            <button
              onClick={() =>
                isPlaying ? pauseAudio() : playAudio(`${process.env.REACT_APP_PIC_STORAGE}/${message.file_path}`)
              }
              className="p-2 rounded-full bg-blue-500 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            >
              {isPlaying ? <Pause size={18} /> : <Play size={18} />}
            </button>
            <span className="ml-2">Audio message</span>
          </div>
        )
      case "location":
        return (
          <div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${message.latitude},${message.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              <img
                src={`https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/pin-s+FF0000(${message.longitude},${message.latitude})/${message.longitude},${message.latitude},14,0/300x200?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || ""}`}
              />
            </a>
          </div>
        )
      default:
        return <p>{message.content}</p>
    }
  }

  const sendLocation = async (latitude: number, longitude: number) => {
    try {
      setIsSending(true)
      const formData = new FormData()
      formData.append("message_type", "location")
      formData.append("latitude", latitude.toString())
      formData.append("longitude", longitude.toString())
      formData.append("need_confirmation", needsConfirmation ? "1" : "0")

      await api.post(`/service-orders/${order.id}/chat`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      await fetchMessages()
    } catch (error) {
      console.error("Error sending location:", error)
      showToastMessage("Failed to send location")
    } finally {
      setIsSending(false)
    }
  }

  const handleSendLocation = (lat: number, lng: number) => {
    sendLocation(lat, lng)
    setShowLocationPicker(false)
  }

  const handleSendAudio = (audioBlob: Blob) => {
    sendMessage(needsConfirmation, null, audioBlob)
    setShowAudioModal(false)
  }

  return (
    <div className="chat-container bg-gray-100 p-4 rounded-lg shadow">
      {showToast && (
        <div className="fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg">{toastMessage}</div>
      )}
      <div ref={chatMessagesRef} className="chat-messages h-96 overflow-y-auto mb-4 p-4 bg-white rounded-lg shadow">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`chat-message mb-4 ${message.sender_type === "agent" ? "text-right" : "text-left"}`}
          >
            <div
              className={`flex items-center  mb-2 ${message.sender_type === "agent" ? "justify-end" : "justify-start"}`}
            >
              {message.sender_type === "agent" && (
                <img
                  src="/icon.png"
                  alt={`${message?.agent?.first_name} ${message?.agent?.last_name}`}
                  className="w-8 h-8 rounded-full mr-2"
                />
              )}
              <span className="font-bold">
                {message.sender_type === "agent"
                  ? `${message.agent ? message.agent.first_name + " " + message.agent.last_name : "Messagerie System"}`
                  : message.sender_type === "client"
                    ? `${order.client.first_name} ${order.client.last_name}`
                    : order.vehicle.plate_number}
              </span>
              {message.sender_type !== "agent" && (
                <img
                  src={
                    message.sender_type === "client"
                      ? `${process.env.REACT_APP_PIC_STORAGE}/${order.client.avatar}`
                      : "/car.png"
                  }
                  alt={
                    message.sender_type === "client"
                      ? `${order.client.first_name} ${order.client.last_name}`
                      : order.vehicle.plate_number
                  }
                  className="w-8 h-8 rounded-full ml-2"
                />
              )}
            </div>
            <div
              className={`p-3 rounded-lg  max-w-[50%] inline-block ${message.sender_type === "agent" ? "bg-blue-100" : "bg-gray-200"}`}
            >
              {renderMessageContent(message)}
            </div>
            <small className={`block text-gray-500 ${message.sender_type === "agent" ? "text-right" : "text-left"}`}>
              {new Date(message.created_at).toLocaleString()}
            </small>
            {message.need_confirmation === 1 && (
              <div className="mt-2 text-sm italic">
                {message.confirmed === 1 ? (
                  <span className="text-green-600">
                    {t("ticketAccepted", {
                      user: message.confirmed_name,
                      date: new Date(message.confirmed_at).toLocaleString(),
                    })}
                  </span>
                ) : message.confirmed === 0 ? (
                  <span className="text-red-600">
                    {t("ticketRefused", {
                      user: message.confirmed_name,
                      date: new Date(message.confirmed_at).toLocaleString(),
                    })}
                  </span>
                ) : (
                  <span className="text-yellow-600">{t("ticketAwaiting")}</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="chat-input flex items-center p-2 bg-white rounded-lg shadow">
        <div className="relative">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            accept="image/*,video/*,audio/*,application/pdf"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
          >
            <Paperclip size={18} />
          </button>
        </div>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={t("type_message")}
          className="flex-grow mx-2 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={() => setShowAudioModal(true)}
          className="p-2 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
        >
          <Mic size={18} />
        </button>
        <button
          onClick={() => setShowLocationPicker(true)}
          className="p-2 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
        >
          <MapPin size={18} />
        </button>
        <button
          onClick={handleSendMessage}
          disabled={isSending}
          className={`ml-2 p-2 rounded-full ${
            isSending ? "bg-gray-400" : "bg-blue-500 hover:bg-blue-600"
          } text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50`}
        >
          {isSending ? (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            <Send size={18} />
          )}
        </button>
      </div>
      <div className="mt-2 flex items-center">
        <input
          type="checkbox"
          id="needsConfirmation"
          checked={needsConfirmation}
          onChange={(e) => setNeedsConfirmation(e.target.checked)}
          className="form-checkbox h-5 w-5 text-blue-600"
        />
        <label htmlFor="needsConfirmation" className="ml-2 text-sm text-gray-700">
          {t("message_need_confirm")}
        </label>
      </div>
      {previewFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white p-4 rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <div className="text-xl font-bold"></div>
              <button
                onClick={() => setPreviewFile(null)}
                className="p-2 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
              >
                <X size={24} />
              </button>
            </div>
            {previewFile.type === "image" ? (
              <img
                src={previewFile.uri || "/placeholder.svg"}
                alt="Preview"
                className="max-w-full max-h-[70vh] object-contain"
              />
            ) : previewFile.type === "video" ? (
              <video src={previewFile.uri} controls className="max-w-full max-h-[70vh]" />
            ) : (
              <iframe src={previewFile.uri} className="w-full h-[70vh]" title="File Preview" />
            )}
          </div>
        </div>
      )}
      <ChatConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false)
          setTempAttachment(null)
          setFilePreview(null)
          setFileType("")
        }}
        onConfirm={(needsConfirmation) => {
          setAttachment(tempAttachment)
          setShowConfirmModal(false)
          sendMessage(needsConfirmation, tempAttachment)
        }}
        fileName={tempAttachment?.name || ""}
        filePreview={filePreview}
        fileType={fileType}
      />
      {showLocationPicker && (
        <LocationPicker onLocationSelected={handleSendLocation} onClose={() => setShowLocationPicker(false)} />
      )}
      <AudioRecordingModal isOpen={showAudioModal} onClose={() => setShowAudioModal(false)} onSend={handleSendAudio} />
    </div>
  )
}

export default Chat

